import {
  default as MUISlider,
  SliderProps as MUISliderProps,
} from '@mui/material/Slider';
import { styled } from '@mui/material/styles';
import isFunction from 'lodash/isFunction';
import { FC, useCallback, useMemo, useState } from 'react';
import SliderLabel from '../atoms/SliderLabel';

interface Mark {
  value: number;
  label?: React.ReactNode;
}

type CustomSliderProps = {
  measurementUnit?: string;
  getMoneyDiscount?: (value: number) => number;
  onValueChange?: (event: Event, newValue: number | number[]) => void;
  defaultSliderValue?: number[];
  label?: string;
  reversed?: boolean;
  leftMarkLabelIndex?: number;
  rightMarkLabelIndex?: number;
};

export type SliderProps = MUISliderProps & CustomSliderProps;

const StyledSlider = styled(MUISlider, {
  skipSx: false,
  shouldForwardProp: (prop) =>
    prop !== 'leftMarkLabelIndex' &&
    prop !== 'rightMarkLabelIndex' &&
    prop !== 'marksLength' &&
    prop !== 'step',
})<SliderProps>(
  ({
    theme: {
      palette: { primary, grey, text },
      shape,
      spacing,
    },
    leftMarkLabelIndex,
    rightMarkLabelIndex,
    marks,
    step,
  }) => ({
    color: primary.main,
    height: '5px',
    marginTop: spacing(4),
    marginBottom: spacing(7),
    '& .MuiSlider-track': {
      height: '5px',
      borderRadius: shape.borderRadius,
    },
    '& .MuiSlider-rail': {
      height: '5px',
      backgroundColor: primary.light,
      borderRadius: shape.borderRadius,
    },
    '& .MuiSlider-thumb': {
      width: '14px',
      height: '14px',
      backgroundColor: primary.main,
      '&$disabled': {
        marginTop: -5,
        width: '14px',
        height: '14px',
      },
    },
    '& .MuiSlider-mark': {
      height: marks && !step ? '10px' : '0px',
    },
    '& .MuiSlider-markLabel': {
      fontSize: '0.8rem',
      transform: 'none !important',
      color: text.secondary,
      [`&[data-index="${leftMarkLabelIndex}"]`]: {
        left: '0 !important',
      },
      [`&[data-index="${rightMarkLabelIndex}"]`]: {
        right: '0 !important',
        left: 'initial !important',
      },
    },
    '& .MuiSlider-valueLabel': {
      fontSize: '0.8rem',
      background: 'transparent',
      textAlign: 'center',
      color: grey[700],
      margin: spacing(0, 'auto'),
    },
  }),
);

const Slider: FC<SliderProps> = ({
  min = 0,
  max = 40,
  step = 0.1,
  onValueChange = () => {},
  measurementUnit = '%',
  getMoneyDiscount,
  valueLabelFormat = null,
  defaultValue,
  value,
  label,
  reversed,
  marks,
  ...restProps
}) => {
  const [_value, _setValue] = useState<number | number[]>(
    value || defaultValue,
  );

  const handleChange = (event: any, newValue: number | number[]) => {
    _setValue(newValue as number[]);
  };

  const isRangeSlider: boolean = useMemo(() => Array.isArray(_value), [_value]);

  const getDefaultMarks = useCallback(
    (): Mark[] => [
      {
        value: min,
        label: `${min}${measurementUnit}`,
      },
      {
        value: max,
        label: `${max.toLocaleString('en-US')}${measurementUnit}`,
      },
    ],
    [min, max, measurementUnit],
  );

  const defaultMarks = getDefaultMarks();

  const marksAmount = Array.isArray(marks)
    ? marks.length - 1
    : defaultMarks.length - 1;

  const leftMarkLabelIndex = reversed ? marksAmount : 0;
  const rightMarkLabelIndex = reversed ? 0 : marksAmount;

  const getValueLabelFormat = useCallback(
    (value, index) => {
      if (isFunction(valueLabelFormat)) {
        return valueLabelFormat(value, index);
      }

      return (
        <SliderLabel
          label={label}
          isRangeSlider={isRangeSlider}
          value={value}
          index={index}
          measurementUnit={measurementUnit}
          getMoneyDiscount={getMoneyDiscount}
        />
      );
    },
    [isRangeSlider, measurementUnit, getMoneyDiscount, valueLabelFormat, label],
  );

  const getValueLabelDisplay = useCallback((): 'on' | 'auto' | 'off' => {
    if (restProps.valueLabelDisplay) {
      return restProps.valueLabelDisplay;
    }

    if (!isRangeSlider) {
      return 'on';
    }

    const [value1, value2] = _value as number[];

    if (getMoneyDiscount) {
      return Math.abs(value1 - value2) < 12 ? 'auto' : 'on';
    }

    return Math.abs(value1 - value2) < 2 ? 'auto' : 'on';
  }, [isRangeSlider, getMoneyDiscount, _value, restProps.valueLabelDisplay]);

  return (
    <StyledSlider
      min={min}
      max={max}
      step={step}
      value={_value}
      valueLabelDisplay={getValueLabelDisplay()}
      valueLabelFormat={getValueLabelFormat}
      onChange={(e, value) => {
        onValueChange(e, value);
        handleChange(e, value);
      }}
      marks={marks || defaultMarks}
      leftMarkLabelIndex={leftMarkLabelIndex}
      rightMarkLabelIndex={rightMarkLabelIndex}
      {...restProps}
    />
  );
};

export default Slider;
